import { useCallback, useEffect, useRef, useState } from 'react';
import { FileData } from '@aperturerobotics/chonky';
import fileService from './galleryService';

export interface FileBrowserData {
  rootFolderId: string;
  fileMap: CustomFileMap;
}

export interface CustomFileData extends FileData {
  parentId?: string;
  childrenIds?: string[];
  image?: any;
}

export interface CustomFileMap {
  [fileId: string]: CustomFileData;
}

export const GALLERY_ROOT_ID = '-1';

/** DB historically used ParentId=-1 for root; gallery also uses virtual root id "-1". */
export const isGalleryRootParent = (parentId?: string | null) =>
  parentId === null ||
  parentId === undefined ||
  parentId === '' ||
  parentId === GALLERY_ROOT_ID ||
  parentId === '0';

const emptyRootMap = (): CustomFileMap => ({
  [GALLERY_ROOT_ID]: {
    id: GALLERY_ROOT_ID,
    name: 'root',
    isDir: true,
    childrenIds: [],
    childrenCount: 0,
  },
});

const normalizeFile = (file: any): CustomFileData => {
  const id = String(file?.id ?? file?.Id ?? '');
  const parentRaw = file?.parentId ?? file?.ParentId;
  const childrenRaw = file?.childrenIds ?? file?.ChildrenIds ?? [];
  const image = file?.image ?? file?.Image ?? undefined;
  const parentId =
    parentRaw === null || parentRaw === undefined || parentRaw === ''
      ? undefined
      : String(parentRaw);

  return {
    id,
    name: file?.name ?? file?.Name ?? '',
    isDir: !!(file?.isDir ?? file?.IsDir),
    parentId: isGalleryRootParent(parentId) ? undefined : parentId,
    childrenIds: (Array.isArray(childrenRaw) ? childrenRaw : []).map(String),
    childrenCount:
      file?.childrenCount ??
      file?.ChildrenCount ??
      (Array.isArray(childrenRaw) ? childrenRaw.length : 0),
    image,
    thumbnailUrl: image?.thumbnailUrl ?? image?.ThumbnailUrl,
  };
};

/** Build Chonky file map with a stable virtual root folder. */
export const buildGalleryFileMap = (records: any[]): CustomFileMap => {
  const entries = (records ?? [])
    .map(normalizeFile)
    .filter((f) => !!f.id);

  const rootChildren = entries
    .filter((e) => isGalleryRootParent(e.parentId))
    .map((e) => e.id);

  const fileMap: CustomFileMap = {
    [GALLERY_ROOT_ID]: {
      id: GALLERY_ROOT_ID,
      name: 'root',
      isDir: true,
      childrenIds: rootChildren,
      childrenCount: rootChildren.length,
    },
  };

  for (const entry of entries) {
    // Skip a real DB row that collides with the virtual root id.
    if (entry.id === GALLERY_ROOT_ID) continue;

    fileMap[entry.id] = {
      ...entry,
      parentId: isGalleryRootParent(entry.parentId)
        ? GALLERY_ROOT_ID
        : entry.parentId,
      childrenIds: (entry.childrenIds ?? []).filter(
        (cid) => cid !== GALLERY_ROOT_ID
      ),
    };
  }

  return fileMap;
};

const useGalleryFileMap = (publisherId: number) => {
  const [timeStamp, setTimeStamp] = useState(new Date());

  const [fileBrowserData, setFileBrowserData] = useState<FileBrowserData>({
    rootFolderId: GALLERY_ROOT_ID,
    fileMap: emptyRootMap(),
  });

  const [currentFolderId, setCurrentFolderId] = useState(GALLERY_ROOT_ID);

  useEffect(() => {
    if (!publisherId) {
      setFileBrowserData({
        rootFolderId: GALLERY_ROOT_ID,
        fileMap: emptyRootMap(),
      });
      setCurrentFolderId(GALLERY_ROOT_ID);
      return;
    }

    let cancelled = false;
    fileService.list(publisherId).then((data: any) => {
      if (cancelled || data?.hasError) return;
      const fileMap = buildGalleryFileMap(data?.records ?? []);
      setFileBrowserData({
        rootFolderId: GALLERY_ROOT_ID,
        fileMap,
      });
      setCurrentFolderId(GALLERY_ROOT_ID);
    });

    return () => {
      cancelled = true;
    };
  }, [timeStamp, publisherId]);

  const resetFileMap = useCallback(() => {
    setCurrentFolderId(GALLERY_ROOT_ID);
  }, []);

  const currentFolderIdRef = useRef(currentFolderId);
  useEffect(() => {
    currentFolderIdRef.current = currentFolderId;
  }, [currentFolderId]);

  const deleteFiles = useCallback((files: CustomFileData[]) => {
    for (const file of files) {
      if (file.id === GALLERY_ROOT_ID) continue;
      fileService.delete(file.id).then(() => {
        setTimeStamp(new Date());
      });
    }
  }, []);

  const moveFiles = useCallback(
    (
      files: CustomFileData[],
      _source: CustomFileData,
      destination: CustomFileData
    ) => {
      for (const file of files) {
        if (file.id === GALLERY_ROOT_ID) continue;
        const parentId =
          destination.id === GALLERY_ROOT_ID ? null : destination.id;
        fileService
          .update({
            id: file.id,
            parentId,
            name: file.name,
            isDir: file.isDir,
          })
          .then(() => {
            setTimeStamp(new Date());
          });
      }
    },
    []
  );

  const createFolder = useCallback(
    (folderName: string) => {
      const parent =
        currentFolderIdRef.current === GALLERY_ROOT_ID
          ? null
          : currentFolderIdRef.current;
      const newFolder = {
        name: folderName,
        isDir: true,
        parentId: parent,
        publisherId,
      };
      fileService.create(newFolder).then(() => {
        setTimeStamp(new Date());
      });
    },
    [publisherId]
  );

  return {
    fileBrowserData,
    currentFolderId,
    setCurrentFolderId,
    resetFileMap,
    deleteFiles,
    moveFiles,
    createFolder,
    setTimeStamp,
  };
};

export default useGalleryFileMap;
